import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, Package } from 'lucide-react';
import { PasswordInput } from '@/components/PasswordInput';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('login');
  const { t: tForgot } = useTranslation('forgotPassword');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('fillAll'));
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('loginSuccess'));
      navigate('/tableau-de-bord');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#1F3F72] flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-[#1F3F72]">DestockPro</span>
          </Link>
          <h1 className="font-heading text-2xl font-bold text-neutral-900">{t('title')}</h1>
          <p className="text-sm text-neutral-500 mt-1">{t('subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-neutral-200 p-6 shadow-sm space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('password')}</Label>
            <PasswordInput
              id="password"
              placeholder={t('passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
            />
            <div className="text-right">
              <Link to="/mot-de-passe-oublie" className="text-xs text-neutral-500 hover:text-[#1F3F72] dark:hover:text-[#F25C05]" data-testid="forgot-password-link">
                {tForgot('title')} ?
              </Link>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full bg-[#1F3F72] hover:bg-[#19325B] text-white"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? t('loggingIn') : <><LogIn className="w-4 h-4 mr-2" /> {t('loginBtn')}</>}
          </Button>
        </form>

        <p className="text-center text-sm text-neutral-500 mt-6">
          {t('noAccount')}{' '}
          <Link to="/inscription" className="font-medium text-[#F25C05] hover:underline" data-testid="login-register-link">
            {t('createAccount')}
          </Link>
        </p>
      </div>
    </div>
  );
}
